import {
  Tables,
  DozenSupabaseClient,
  TablesInsert,
} from "../../database.types";
import { App } from "../types/types";

// Team CRUD operations
export const teamService = {
  async getAllLeagueTeams(
    supabase: DozenSupabaseClient,
    league_id: string,
  ): Promise<App.TeamWithMembers[]> {
    const { data, error } = await supabase
      .from("teams")
      .select(
        "*, league_members!league_members_team_id_fkey(profiles!league_members_user_id_fkey1(*))",
      )
      .eq("league_id", league_id)
      .order("name");

    if (error) {
      console.error(`Error fetching teams for league ${league_id}:`, error);
      throw error;
    }

    return (data || []).map(({ league_members, ...team }) => ({
      ...team,
      members: league_members.map((m) => m.profiles),
    }));
  },

  async getById(
    supabase: DozenSupabaseClient,
    id: string,
  ): Promise<Tables<"teams"> | null> {
    const { data, error } = await supabase
      .from("teams")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(`Error fetching team with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  async getByIdWithMembers(
    supabase: DozenSupabaseClient,
    id: string,
  ): Promise<App.TeamWithMembers | null> {
    const { data, error } = await supabase
      .from("teams")
      .select(
        "*, league_members!league_members_team_id_fkey(profiles!league_members_user_id_fkey1(*))",
      )
      .eq("id", id)
      .single();

    if (error) {
      console.error(`Error fetching team with members for id ${id}:`, error);
      throw error;
    }

    if (!data) return null;

    const { league_members, ...team } = data;
    return {
      ...team,
      members: league_members.map((m) => m.profiles),
    };
  },

  async create(
    supabase: DozenSupabaseClient,
    team: TablesInsert<"teams">,
  ): Promise<Tables<"teams">> {
    const { data, error } = await supabase
      .from("teams")
      .insert(team)
      .select()
      .single();

    if (error) {
      console.error("Error creating team:", error);
      throw error;
    }

    return data;
  },

  async update(
    supabase: DozenSupabaseClient,
    id: string,
    updates: Partial<Omit<Tables<"teams">, "id" | "created_at" | "updated_at">>,
  ): Promise<Tables<"teams">> {
    const { data, error } = await supabase
      .from("teams")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating team with id ${id}:`, error);
      throw error;
    }

    return data;
  },

  async getLeagueStandings(
    supabase: DozenSupabaseClient,
    league_id: string,
  ): Promise<Tables<"teams">[]> {
    const { data, error } = await supabase
      .from("teams")
      .select("*")
      .eq("league_id", league_id)
      .order("wins", { ascending: false });

    if (error) {
      console.error("Error fetching league standings:", error);
      throw error;
    }

    return data || [];
  },

  async delete(supabase: DozenSupabaseClient, id: string): Promise<void> {
    const { error } = await supabase.from("teams").delete().eq("id", id);

    if (error) {
      console.error(`Error deleting team with id ${id}:`, error);
      throw error;
    }
  },

  async uploadLogo(supabase: DozenSupabaseClient, file: File): Promise<string> {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `team-logos/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("team-logos")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Error uploading logo:", uploadError);
      throw uploadError;
    }

    // Create a file record in the database
    const { data: fileData, error: fileRecordError } = await supabase
      .from("files")
      .insert({
        file_name: fileName,
        file_type: file.type,
        file_path: filePath,
        file_size: file.size,
      })
      .select()
      .single();

    if (fileRecordError) {
      console.error("Error creating file record:", fileRecordError);
      throw fileRecordError;
    }

    return fileData.id;
  },
};
